/**
 * Scenario key constants for the FAD boost ruleset feature.
 * These keys are the shared contract with BoostRulesetService (Java).
 * A rename on either side breaks the other — never rename without coordinating.
 *
 * R-02 (AHSMKTG-4018): VPC boost-ruleset keys (vpc-*) are excluded from this file.
 * The VPC_QUALIFYING_TERMS, VPC_QUALIFYING_SPECIALTIES, and isVpcQualifyingSearch
 * additions below are eligibility helpers — not boost-ruleset keys — and are outside
 * R-02's scope (AHSMKTG-4027).
 */
export const FAD_BOOST_RELEVANCY_NO_LOCATION = 'FAD_BOOST_RELEVANCY_NO_LOCATION';
export const FAD_BOOST_RELEVANCY_WITH_LOCATION = 'FAD_BOOST_RELEVANCY_WITH_LOCATION';
export const FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION = 'FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION';
export const FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION = 'FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION';
export const FAD_BOOST_OUTSIDE_MATCHES_RELEVANCY = 'FAD_BOOST_OUTSIDE_MATCHES_RELEVANCY';
export const FAD_BOOST_OUTSIDE_MATCHES_NEXT_AVAILABLE = 'FAD_BOOST_OUTSIDE_MATCHES_NEXT_AVAILABLE';

// VPC scenario key constants (AHSMKTG-4019). Shared contract with BoostRulesetService (Java).
export const FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION_VPC = 'FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION_VPC';
export const FAD_BOOST_RELEVANCY_WITH_LOCATION_VPC = 'FAD_BOOST_RELEVANCY_WITH_LOCATION_VPC';
// AHSMKTG-4136: Next Available + location + NJ + VPC-qualifying searches get their own key
// (previously routed to FAD_BOOST_RELEVANCY_WITH_LOCATION_VPC via the TC-10 workaround), so
// each sort mode can be tuned independently via Cloud Manager env var.
export const FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION_VPC =
    'FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION_VPC';

/**
 * All nine expected boost ruleset scenario keys, in declaration order.
 * Six base keys (AHSMKTG-4018) + three VPC variants (AHSMKTG-4019, AHSMKTG-4136).
 * Used for mount-time validation of the boostRulesets prop.
 */
export const FAD_BOOST_SCENARIO_KEYS = [
    FAD_BOOST_RELEVANCY_NO_LOCATION,
    FAD_BOOST_RELEVANCY_WITH_LOCATION,
    FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION,
    FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION,
    FAD_BOOST_OUTSIDE_MATCHES_RELEVANCY,
    FAD_BOOST_OUTSIDE_MATCHES_NEXT_AVAILABLE,
    FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION_VPC,
    FAD_BOOST_RELEVANCY_WITH_LOCATION_VPC,
    FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION_VPC,
];

/**
 * Pure function: derives the active FAD boost scenario key from sort mode, location,
 * outsideMatches state, search term, specialty filters, and NJ in-market flag.
 *
 * isOutsideMatches takes precedence over hasLocation and all VPC branches — the
 * outsideMatches keys have no with/without-location variants and no VPC variants.
 *
 * An unexpected selectedSort value falls back to the Relevancy branch (conservative
 * safe default — no modifier override applied). In non-production builds, a console.warn
 * is logged.
 *
 * @param {string}           selectedSort    - 'Relevancy' | 'Next Available' (selectedSort.current)
 * @param {boolean}          hasLocation     - !!(urlParams['zip-code'] || urlParams['city'])
 * @param {boolean}          isOutsideMatches - urlParams['isOutsideMatches'] || false
 * @param {string|undefined} term            - urlParams.term (4th arg — search term)
 * @param {string[]|undefined} specialties   - urlParams.specialty (5th arg — active specialty filters)
 * @param {boolean}          isNJ            - Pre-computed by caller via isInMarketNJ(geo). Gates the
 *                                             FAD_BOOST_RELEVANCY_WITH_LOCATION_VPC and
 *                                             FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION_VPC branches;
 *                                             out-of-market (e.g. PA) falls through to the non-VPC
 *                                             WITH_LOCATION key for the active sort mode.
 * @returns {string} One of the nine FAD_BOOST_* scenario key constants
 */
export function deriveScenarioKey(
    selectedSort,
    hasLocation,
    isOutsideMatches,
    term,
    specialties,
    isNJ,
) {
    // Step 1: isOutsideMatches takes absolute precedence — no VPC variants apply here.
    if (isOutsideMatches) {
        return selectedSort === 'Next Available'
            ? FAD_BOOST_OUTSIDE_MATCHES_NEXT_AVAILABLE
            : FAD_BOOST_OUTSIDE_MATCHES_RELEVANCY;
    }

    // Step 2: Next Available branch.
    if (selectedSort === 'Next Available') {
        if (!hasLocation && isVpcQualifyingSearch({ term, specialties })) {
            return FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION_VPC; // Scenario 1 VPC branch
        }
        // TC-10 fix (AHSMKTG-4019, re-keyed AHSMKTG-4136): Next Available + hasLocation + NJ +
        // qualifying searches include VPCs in results. Route to the Next Available VPC key (not
        // the Relevancy VPC key) so vpcDeboost RF keeps VPCs behind physical providers while
        // remaining independently tunable per sort mode.
        if (hasLocation && isNJ && isVpcQualifyingSearch({ term, specialties })) {
            return FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION_VPC;
        }
        return hasLocation
            ? FAD_BOOST_NEXT_AVAILABLE_WITH_LOCATION
            : FAD_BOOST_NEXT_AVAILABLE_NO_LOCATION;
    }

    // Step 3: Relevancy branch (default). Unexpected selectedSort falls through here.
    if (process.env.NODE_ENV !== 'production' && selectedSort !== 'Relevancy') {
        // eslint-disable-next-line no-console
        console.warn(
            `[deriveScenarioKey] Unexpected selectedSort value: ${selectedSort}. Falling back to Relevancy.`,
        );
    }
    if (hasLocation && isNJ && isVpcQualifyingSearch({ term, specialties })) {
        return FAD_BOOST_RELEVANCY_WITH_LOCATION_VPC; // Scenario 2 VPC branch
        // isNJ ensures Scenario 4 (out-of-market, e.g. PA) falls through to the base key.
    }
    return hasLocation ? FAD_BOOST_RELEVANCY_WITH_LOCATION : FAD_BOOST_RELEVANCY_NO_LOCATION;
}

// ---------------------------------------------------------------------------
// VPC Banner eligibility helpers (AHSMKTG-4027 / AHSMKTG-4019 prereq)
// ---------------------------------------------------------------------------

export const VPC_QUALIFYING_TERMS = ['', 'primary care', 'internal medicine'];

// Title-case exact match — values confirmed from Coveo facetId:'specialty' API response.
export const VPC_QUALIFYING_SPECIALTIES = ['Primary Care', 'Internal Medicine'];

/**
 * Returns true when the current search qualifies for VPC routing.
 * When specialty filters are active, at least one must be VPC-qualifying — a non-VPC
 * specialty (e.g. Acupuncture) suppresses the blank-term gate.
 * When no specialties are active, a qualifying term suffices.
 *
 * @param {object}   opts
 * @param {string}   opts.term        - Current Coveo query term (querySummaryState.query)
 * @param {string[]} opts.specialties - Selected specialty facet values (title-case from Coveo API)
 * @returns {boolean}
 */
export function isVpcQualifyingSearch({ term, specialties }) {
    const filteredSpecialties = (specialties ?? []).filter(Boolean);
    // When specialty filters are active, at least one must be VPC-qualifying.
    // A non-VPC specialty (e.g. Acupuncture) overrides the blank-term gate — VPC
    // providers cannot appear in non-VPC specialty searches, so VPC routing is suppressed.
    if (filteredSpecialties.length > 0) {
        return filteredSpecialties.some((s) =>
            VPC_QUALIFYING_SPECIALTIES.includes(s.split(';')[0]),
        );
    }
    return VPC_QUALIFYING_TERMS.includes((term ?? '').toLowerCase().trim());
}

// ---------------------------------------------------------------------------
// VPC Banner display gate (AHSMKTG-4137)
// ---------------------------------------------------------------------------

// Exact Coveo facetId:'specialtyservice' value that qualifies a blank-term search.
export const VPC_BANNER_QUALIFYING_SPECIALTY_SERVICES = ['Virtual Care'];

// Banner-only term list (AHSMKTG-4108). Deliberately NOT derived from VPC_QUALIFYING_TERMS: that
// constant also drives boost scenario keys and the VPC query clause, which must not change.
// The zero-state (blank+no-filters) gate is handled separately in isVpcBannerQualifyingSearch.
export const VPC_BANNER_QUALIFYING_TERMS = [
    'primary care',
    'internal medicine',
    'virtual primary care',
    'family medicine',
];

// Banner-only specialty list (AHSMKTG-4108) — same reasoning as the terms above.
export const VPC_BANNER_QUALIFYING_SPECIALTIES = [
    'Primary Care',
    'Internal Medicine',
    'Family Medicine',
];

// Selecting any of these suppresses the banner, even when a qualifying term/specialty is paired.
export const VPC_BANNER_EXCLUDED_SPECIALTIES = ['Pediatrics'];

/**
 * Returns true when any selected specialty (Coveo ;N suffix tolerated) is banner-excluded.
 *
 * @param {string[]} specialties - Selected specialty facet values (may carry ;N suffix)
 * @returns {boolean}
 */
export function hasVpcBannerExcludedSpecialty(specialties) {
    return (specialties ?? []).some((s) =>
        VPC_BANNER_EXCLUDED_SPECIALTIES.includes(s.split(';')[0]),
    );
}

/**
 * Returns true when the current search qualifies the VPC Banner to appear,
 * using the updated gate introduced in AHSMKTG-4137.
 *
 * Use this function to gate banner display. isVpcQualifyingSearch remains
 * unchanged and continues to govern scenario routing and Scenario 3 detection.
 *
 * Qualifying conditions (OR-independent):
 *   1. qualifying specialty selected (Primary Care / Internal Medicine / Family Medicine)
 *   2. Virtual Care specialtyservice selected
 *   3. VPC checkbox checked (?vpc=true)
 *   4. non-blank qualifying term ('primary care' / 'internal medicine' / 'virtual primary care' /
 *      'family medicine')
 *   5. blank term with no filters selected at all (zero-state — AHSMKTG-4137 additional requirement)
 *
 * Blank term with any non-qualifying filter selected (e.g. Bariatrics specialty) still suppresses.
 * Selecting Pediatrics suppresses the banner unconditionally (AHSMKTG-4108).
 *
 * @param {object}   opts
 * @param {string}   opts.term              - Current Coveo query term
 * @param {string[]} opts.specialties       - Selected specialty facet values (may carry ;N suffix)
 * @param {string[]} opts.specialtyServices - Selected specialtyservice facet values
 * @param {boolean}  opts.isVpcChecked      - Whether the VPC checkbox (hasvirtualprimarycare) is checked
 * @returns {boolean}
 */
export function isVpcBannerQualifyingSearch({
    term,
    specialties,
    specialtyServices,
    isVpcChecked,
}) {
    const sa = specialties ?? [];
    const ssa = specialtyServices ?? [];

    // Excluded specialty (Pediatrics) wins over every other signal (AHSMKTG-4108).
    if (hasVpcBannerExcludedSpecialty(sa)) return false;

    // Qualifying specialty — OR-independent; applies regardless of term.
    if (sa.some((s) => VPC_BANNER_QUALIFYING_SPECIALTIES.includes(s.split(';')[0]))) return true;

    // Virtual Care specialtyservice — OR-independent.
    if (ssa.some((s) => VPC_BANNER_QUALIFYING_SPECIALTY_SERVICES.includes(s.split(';')[0])))
        return true;

    // VPC checkbox — OR-independent.
    if (isVpcChecked) return true;

    const normalizedTerm = (term ?? '').toLowerCase().trim();

    // Non-blank qualifying terms qualify on their own.
    if (VPC_BANNER_QUALIFYING_TERMS.includes(normalizedTerm)) return true;

    // Zero-state: blank term with no filters selected at all → show banner (AHSMKTG-4137 additional
    // requirement). Blank term with any selected filter (even non-qualifying) still suppresses.
    // isVpcChecked is implicitly false here — handled by the early return above.
    return normalizedTerm === '' && !sa.length && !ssa.length;
}
